/** @class ManageItemPermissionsModal
 *
 * @desc
 * Modal for managing advisor/firm item permissions
 *
 */

import {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Map, List} from 'immutable'

import {Checkbox, FormControlLabel} from '@mui/material'

import ButtonGroup from '../../../../../../shared_components/core/buttons/ButtonGroup'
import ConfirmationCloser from '../../../../../../shared_components/ConfirmationCloser'
import Modals from '../../../../../../shared_components/core/modals/Modals'
import ItemPermissionsSelectForm from '../../../../../../shared_components/item_permissions/ItemPermissionsSelectForm'

import {Cancel, Accept} from '../../../blocks/Buttons'
import {ModalHeader} from '../../../../../../shared_components/core/modals/ModalHeaders'
import {addPossessive, arraysAreEqual} from '../../../../../../lib/tools'
import {requestedItemPermissions} from '../../../../../../lib/itemPermissionTools'
import {useNotificationContext} from '../../../../../../shared_components/notifications/NotificationContext'

import './manageItemPermissionsModal.scss'

const ManageItemPermissionsModal = ({
  isFuneralHome,
  items,
  name,
  ownership,
  updateResource
}) => {
  const [isDirty, setIsDirty] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [checkedItemIds, setCheckedItemIds] = useState([])

  const [
    hasDeputyPermission, setHasDeputyPermission
  ] = useState(ownership.get('permission-to-add-deputy') || false)

  const {alwaysNotify} = useNotificationContext()

  const toggleModal = () => {
    setIsDirty(false)
    setShowModal(prevShowModal => !prevShowModal)
    setProcessing(false)
    setCheckedItemIds(ownership.get('edit-item-ids').toArray())
  }

  const closer = () => (
    <ConfirmationCloser
      close={toggleModal}
      confirmButtonText={'Discard'}
      confirmClose={isDirty}
      headingText={'Discard changes you\'ve made?'}
    />
  )

  const modalHeaderNote = useMemo(() => {
    if (ownership.get('manageable-edit-item-ids').size !== items.size)
      return `Note: You can't give ${name} permission to edit the sections you marked as "After Death" when you added ${name} as a Deputy.`
  }, [ownership, isFuneralHome, items, name])

  const deputyPermissionLabel = () => (
    <span className='deputy-permission-label'>
      I allow the Everplans Concierge to share information on my behalf from the
      sections they have access to.
    </span>
  )

  const closeAndNotify = () => {
    toggleModal()

    alwaysNotify.longSuccess(
      `${addPossessive(name)} permissions have been updated.`
    )
  }

  const submitPermissions = () => {
    setProcessing(true)

    const payload = {
      type: isFuneralHome ? 'quarterback-ownerships' : 'firm-ownerships',
      id: ownership.get('id'),
      attributes: {
        'item-permissions': requestedItemPermissions({
          itemIds: checkedItemIds,
          permissionType: 'edit'
        }),
        ...(isFuneralHome && {'permission-to-add-deputy': hasDeputyPermission})
      }
    }

    updateResource(payload).then(() => closeAndNotify())
  }

  const handleDeputyPermissionChange = event => {
    setHasDeputyPermission(event.target.checked)

    setIsDirty(
      event.target.checked !== ownership.get('permission-to-add-deputy')
    )
  }

  const onCheck = itemIds => {
    setCheckedItemIds(itemIds)

    setIsDirty(!arraysAreEqual(
      ownership.get('edit-item-ids').toArray(),
      itemIds
    ))
  }

  return (
    <span className='manage-item-permissions-modal'>
      <Accept onClick={toggleModal}>Manage</Accept>
      <Modals.FullHeightModalLarge
        closerComponent={closer}
        showModal={showModal}>
        <ModalHeader
          heading={`Which sections should ${name} be able to edit?`}
          note={modalHeaderNote}
        />
        <ItemPermissionsSelectForm
          checkedItemIds={checkedItemIds}
          onCheck={onCheck}
          permittedItemIds={ownership.get('manageable-edit-item-ids')}
        />
        {/* TODO - Uncomment the following code when TK-2124 is complete - JS */}
        {/* {
          isFuneralHome &&
          <FormControlLabel
            className='deputy-permission-checkbox'
            control={
              <Checkbox
                checked={hasDeputyPermission}
                checkedIcon={<i className='fa fa-fw fa-check-square' />}
                disableRipple
                icon={<i className='fa fa-fw fa-square-o' />}
                inputProps={{'aria-label': 'controlled'}}
                onChange={handleDeputyPermissionChange}
                sx={{
                  '&.Mui-checked': {
                    color: '#15A9FF'
                  },
                  '& .fa-square-o': {
                    color: '#262626'
                  }
                }}
              />
            }
            disabled={processing}
            label={deputyPermissionLabel()}
          />
        } */}
        <ButtonGroup>
          <Cancel onClick={toggleModal} disabled={processing} />
          <Accept
            disabled={!isDirty}
            onClick={submitPermissions}
            processing={processing}>
            Save
          </Accept>
        </ButtonGroup>
      </Modals.FullHeightModalLarge>
    </span>
  )
}

ManageItemPermissionsModal.propTypes = {
  isFuneralHome: PropTypes.bool,
  items: PropTypes.instanceOf(List),
  name: PropTypes.string,
  ownership: PropTypes.instanceOf(Map),
  updateResource: PropTypes.func
}

export default ManageItemPermissionsModal
